import { Alert, Button, Typography, useTheme } from '@mui/material';
import { Door } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React from 'react';
import { LocationsWithDoorsState } from 'state/types';
import styled from 'styled-components';
import LockGroup from 'ui/molecules/LockGroup/LockGroup';
import Search from 'ui/molecules/Search/SearchMUI';
import ComponentWrapper from 'ui/templates/ComponentWrapper/ComponentWrapper';

export interface Props {
  inputOnKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  handleOpenCreateLockGroupModal: () => void;
  handleOpenEditLockGroupModal: (locationId: string, lockGroupName: string, selectedDoors: Array<Door>) => void;
  searchGroupInputValue: string;
  searchGroupInputOnChange: (value: string) => void;
  handleOnDeleteLockGroupIconClick: (locationId: string) => void;
  doorGroups: LocationsWithDoorsState;
}

const LockGroupWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-direction: column;
  overflow-y: overlay;
  overflow-x: hidden;
  height: 40vh;

  ::-webkit-scrollbar-track {
    background-color: ${(props) => props.theme.colors.b5};
  }

  ::-webkit-scrollbar-thumb {
    background-color: ${(props) => props.theme.colors.b6};
  }
`;

const ManageLockGroups: React.FC<React.PropsWithChildren<Props>> = ({
  handleOpenCreateLockGroupModal,
  handleOnDeleteLockGroupIconClick,
  handleOpenEditLockGroupModal,
  doorGroups,
  searchGroupInputValue,
  searchGroupInputOnChange
}) => {
  const theme = useTheme();
  const { translation } = useTranslation();
  return (
    <ComponentWrapper flexDirection="column" gap="1.5rem">
      <ComponentWrapper gap="1.5rem" flexDirection="column">
        <Button
          onClick={handleOpenCreateLockGroupModal}
          id="drawer-create-lock-group-button"
          variant="contained"
          fullWidth
          color="primary"
          size="large"
        >
          {translation.create_lock_group}
        </Button>
        <Search
          placeholder={translation.search_lock_group}
          value={searchGroupInputValue}
          onChange={(event) => searchGroupInputOnChange(event.target.value)}
          sizeSearch="medium"
          id="drawer-input-search-group"
        />
      </ComponentWrapper>
      <ComponentWrapper flexDirection="column">
        <LockGroupWrapper>
          {doorGroups.length === 0 && searchGroupInputValue === '' && (
            <Alert
              severity="error"
              variant="standard"
              icon={false}
              sx={{
                backgroundColor: 'rgba(99, 102, 111, 0.04)',
                border: '1px solid #444',
                justifyContent: 'center'
              }}
            >
              <Typography variant="body1" style={{ color: theme.palette.text.disabled }}>
                {`${translation.no_groups_created}`}
              </Typography>
            </Alert>
          )}
          {doorGroups.map((group, index) => (
            <LockGroup
              id={`lock-group-${index}`}
              deleteIconId={`row-${index}-delete-icon`}
              lockGroupName={group.name}
              numberOfSelectedDoors={group.doors.length}
              onDeleteClickIcon={() => handleOnDeleteLockGroupIconClick(group.id)}
              onClick={() => handleOpenEditLockGroupModal(group.id, group.name, group.doors)}
            />
          ))}
          {doorGroups.length === 0 && searchGroupInputValue !== '' && (
            <ComponentWrapper justifyContent="center">
              <Typography variant="body2">{translation.no_results}</Typography>
            </ComponentWrapper>
          )}
        </LockGroupWrapper>
      </ComponentWrapper>
    </ComponentWrapper>
  );
};

export default ManageLockGroups;
